import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {CreateUserGroupModel, UserGroupWithUsersModel, UserModel, UserRole, USER_ROLE_CLIENT_ADMIN, USER_ROLE_CLIENT_NORMAL, USER_ROLE_PRODUCTION_ADMIN, USER_ROLE_PRODUCTION_NORMAL, USER_ROLE_PRODUCTION_OFFSHORE, USER_ROLE_SUUMO_ADMIN, USER_ROLE_SUUMO_CUSTOMER, USER_ROLE_SUUMO_NORMAL} from '../models/user.model';
import {Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {HttpUtilsService} from '../_base/crud';
import queryString from 'query-string';
import {ItemsResponse} from '../_base/crud/models/query-models/items-response.model';
import { OKResponse } from '../_base/crud/models/query-models/query-results.model';

const API_USER_GROUP_URL = `${environment.pathToAPI}/api/v1/user_groups`;
const API_USER_URL = `${environment.pathToAPI}/api/v1/users`;

export const PERMISSION = [
    "usersManagementModuleAccess",
    "canReadProjectData",
    "canEditProjectData",
    "canDeleteProjects",
    "canEditProjectStatus",
    "canReadUserData",
    "canEditUserData",
    "canDeleteUserData",
    "conversationsModuleAccess",
    "canReadConversationData",
    "canEditConversationData",
    "canDeleteConversations",
    "canAddConversations",
    "systemManagementModuleAccess"
] as const;
export type Permission = typeof PERMISSION[number];

const CLIENT_ADMIN_PEMISSIONS = [1, 2, 10, 3, 4, 7, 8, 9, 11, 13].map((index) => PERMISSION[index]);
const CLIENT_NORMAL_PERMISSIONS = [1, 2, 10, 3, 7, 11].map((index) => PERMISSION[index]);
const PRODUCTION_ADMIN_PERMISSONS = [1, 2, 10, 3, 4, 5, 6, 7, 8, 9, 11, 12, 13, 14, 15].map((index) => PERMISSION[index]);
const PRODUCTION_NORMAL_PERMISSONS = [1, 2, 10, 3, 7, 11, 14].map((index) => PERMISSION[index]);
const PRODUCTION_OFFSHORE_PERMISSIONS = [] as Permission[];
const SUUMO_ADMIN_PEMISSIONS = [] as Permission[];
const SUUMO_NORMAL_PERMISSONS = [] as Permission[];
const SUUMO_CUSTOMER_PERMISSONS = [] as Permission[];

@Injectable()
export class UsersService {
    constructor(private http: HttpClient, private httpUtils: HttpUtilsService) {
    }

    getPrivateChatUsers(): Observable<number[]> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.get<number[]>(API_USER_URL + "/private_chat_users", {headers: httpHeaders});
    }

    findUserGroup(userGroupId: number): Observable<UserGroupWithUsersModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.get<UserGroupWithUsersModel>(API_USER_GROUP_URL + `/${userGroupId}`, {headers: httpHeaders});
    }

    findUserGroups(searchText = '', offset = 0, limit = 10): Observable<ItemsResponse<UserGroupWithUsersModel>> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const query = queryString.stringify({
            searchText,
            offset,
            limit,
        });
        return this.http.get<ItemsResponse<UserGroupWithUsersModel>>(API_USER_GROUP_URL + '/?' + query, {headers: httpHeaders});
    }

    createUserGroup(userGroup: CreateUserGroupModel, addUserIds: number[] = []): Observable<UserGroupWithUsersModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<UserGroupWithUsersModel>(
            API_USER_GROUP_URL,
            {...userGroup, addUserIds},
            {headers: httpHeaders});
    }

    updateUserGroup(userGroup: UserGroupWithUsersModel,
                    addUserIds: number[] = [],
                    removeUserIds: number[] = []): Observable<UserGroupWithUsersModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.put<UserGroupWithUsersModel>(
            API_USER_GROUP_URL + `/${userGroup.id}`,
            {...userGroup, addUserIds, removeUserIds},
            {headers: httpHeaders});
    }

    registerUserGroups(userId: number, addUserGroupIds: number[], removeUserGroupIds: number[]): Observable<UserModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.put<UserModel>(
            API_USER_URL + `/${userId}/groups`,
            {addUserGroupIds, removeUserGroupIds},
            {headers: httpHeaders});
    }

    deleteUserGroups(deleteUserGroupIds: number[]): Observable<OKResponse> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.put<{ok: boolean}>( // なぜかdeleteにbodyが渡せない。
            API_USER_GROUP_URL + '/',
            {deleteIds: deleteUserGroupIds},
            {headers: httpHeaders});
    }
    
    resolvePermissions(role: UserRole | undefined): Permission[] {
        switch (role) {
            case USER_ROLE_CLIENT_ADMIN:
                return CLIENT_ADMIN_PEMISSIONS;
            case USER_ROLE_CLIENT_NORMAL:
                return CLIENT_NORMAL_PERMISSIONS;
            case USER_ROLE_PRODUCTION_ADMIN:
                return PRODUCTION_ADMIN_PERMISSONS;
            case USER_ROLE_PRODUCTION_NORMAL:
                return PRODUCTION_NORMAL_PERMISSONS;
            case USER_ROLE_PRODUCTION_OFFSHORE:
                return PRODUCTION_OFFSHORE_PERMISSIONS;
            case USER_ROLE_SUUMO_ADMIN:
                return SUUMO_ADMIN_PEMISSIONS;
            case USER_ROLE_SUUMO_NORMAL:
                return SUUMO_NORMAL_PERMISSONS;
            case USER_ROLE_SUUMO_CUSTOMER:
                return SUUMO_CUSTOMER_PERMISSONS;
            default:
                return [];
        }
    }

}

